import { create } from 'zustand';
import type { User } from 'firebase/auth';

/**
 * Auth store — mirrors Flutter's AppStateNotifier + GenericHelpers MFA flag.
 * Tracks Firebase user, MFA verification and auth initialisation.
 */

interface AuthState {
  user: User | null;
  isAuthorized: boolean;
  isMfaVerified: boolean;
  isAuthLoading: boolean;

  setUser: (user: User | null) => void;
  setMfaVerified: (value: boolean) => void;
  setAuthLoading: (value: boolean) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthorized: false,
  isMfaVerified: localStorage.getItem('isMfaVerified') === 'true',
  isAuthLoading: true,

  setUser: (user) => set({
    user,
    isAuthorized: user !== null && (user.emailVerified ?? false),
  }),

  /**
   * Mirrors Flutter GenericHelpers.setIsMfaVerified() — persisted (SharedPreferences)
   * until the next login or logout resets it.
   */
  setMfaVerified: (value) => {
    localStorage.setItem('isMfaVerified', String(value));
    set({ isMfaVerified: value });
  },

  setAuthLoading: (value) => set({ isAuthLoading: value }),

  clearAuth: () => {
    localStorage.removeItem('isMfaVerified');
    set({
      user: null,
      isAuthorized: false,
      isMfaVerified: false,
      isAuthLoading: false,
    });
  },
}));
